import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { readOnboardingNavigationState } from '@/pages/onboarding/onboarding-navigation-state';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { z } from 'zod';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { LabeledInput } from '@/components/labeled-input';
import { Button } from '@/components/ui/button';
import {
  Loader2,
  CheckCircle2,
  AlertCircle,
  Sparkles,
  Info,
} from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { usePatchTenantCreationRequest } from '@/hooks/use-patch-tenant-request';
import { useTenantCreationRequest } from '@/hooks/use-tenant-creation-request';
import { useDebouncedValue } from '@/hooks/use-debounced-value';
import { useMediaQuery } from '@/hooks/use-mobile';
import { EDITOR_DESKTOP_MEDIA_QUERY } from '@/components/app-editor/use-editor-chrome';
import {
  OnboardingPreviewProvider,
  useOnboardingPreview,
  useSetOnboardingPreview,
} from '@/pages/onboarding/onboarding-preview-context';
import {
  ZoomedIPhoneMockup,
  ZOOM_TRANSITION_DURATION_MS,
} from '@/pages/onboarding/mockup/zoomed-iphone-mockup';
import { HOME_ICON_FOCUS } from '@/pages/onboarding/mobile-studio-geometry';
import { markStudioModalTransition } from '@/pages/onboarding/studio-modal-transition';
import { HomeScreenScene } from '@/pages/onboarding/mockup/scenes/home-screen-scene';
import {
  OnboardingPageLayout,
  OnboardingPageHeader,
  OnboardingPageContent,
  OnboardingPageFooter,
} from '@/pages/onboarding/onboarding-page-layout';
import { OnboardingStepsIndicator } from '@/pages/onboarding/onboarding-steps-indicator';

// Same URL-validation/domain-normalization pattern as the old wizard's
// `extractDomainFromUrl` (admin/src/pages/signup/1-app-customization.page.tsx:28-38).
const DOMAIN_REGEX =
  /^(?:https?:\/\/)?((?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,})(:?\/.*)?$/;

function extractDomainFromUrl(url: string): string {
  const result = DOMAIN_REGEX.exec(url)?.[1];

  return result ?? url;
}

// Light input mask: strips protocol and whitespace as the lead types, so
// the field always reads as a clean domain instead of a pasted URL. Keeps
// any subdomain (including www) as typed — it's meaningful for stores that
// actually live at a subdomain, not just a prefix to strip.
function maskStoreUrl(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '')
    .replace(/^https?:\/\//, '');
}

type StoreUrlCheck =
  | { status: 'idle' }
  | { status: 'valid' }
  | { status: 'invalid'; message: string };

// Live, field-level feedback shown as the lead types — separate from the
// zod schema (which still gates actual submission).
function checkStoreUrl(value: string): StoreUrlCheck {
  // Too short to give meaningful feedback yet — avoids flashing an error
  // the instant the lead types the first character or two.
  if (value.length < 3) {
    return { status: 'idle' };
  }

  if (DOMAIN_REGEX.test(value)) {
    return { status: 'valid' };
  }

  if (!value.includes('.')) {
    return {
      status: 'invalid',
      message: 'Falta a extensão do domínio, como .com ou .com.br',
    };
  }

  if (/\.[a-zA-Z]{2,}$/.test(value) === false) {
    return {
      status: 'invalid',
      message: 'Termine o domínio com algo como .com ou .com.br',
    };
  }

  return {
    status: 'invalid',
    message: 'Verifique se o link está completo e correto',
  };
}

const FormSchema = z.object({
  appName: z.string().min(1, 'Campo obrigatório'),
  storeUrl: z
    .string()
    .min(1, 'Campo obrigatório')
    .refine((str) => DOMAIN_REGEX.test(str), 'URL inválida')
    .transform(extractDomainFromUrl),
  appContactEmail: z.string().email('E-mail inválido'),
});

type FormValuesInput = z.input<typeof FormSchema>;
type FormValuesOutput = z.output<typeof FormSchema>;

export default function AppNameAndLinkPage() {
  return (
    <OnboardingPreviewProvider>
      <AppNameAndLinkPageContent />
    </OnboardingPreviewProvider>
  );
}

// How long after mount the zoom-in starts — gives the choose-store →
// home-screen load (ChooseStoreMockupScene's unlock animation + the page's
// own view transition) a beat to finish settling first.
const ZOOM_IN_DELAY_MS = 500;

function AppNameAndLinkPageContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const patchCreationRequest = usePatchTenantCreationRequest();
  const persistedRequest = useTenantCreationRequest();
  const preview = useOnboardingPreview();
  const setPreview = useSetOnboardingPreview();
  const shouldReduceMotion = useReducedMotion();
  const isDesktop = useMediaQuery(EDITOR_DESKTOP_MEDIA_QUERY);

  // Normally arrives unzoomed (continuing from ChooseStorePage's unlock
  // animation, which never zooms) and flips to zoomed shortly after mount
  // (see ZOOM_IN_DELAY_MS) — ZoomedIPhoneMockup animates the change. But
  // arriving back from AppLogoPage's "Voltar" is different: that page stays
  // zoomed in the whole time (it never zooms out to navigate backward), so
  // this page should just continue showing the same zoomed-in icon from its
  // very first frame instead of dropping to rest and zooming in all over
  // again.
  const arrivingFromLogoBack = Boolean(
    readOnboardingNavigationState(location.state).fromAppLogoBack
  );
  const [isZoomed, setIsZoomed] = useState(arrivingFromLogoBack);
  const [isLeaving, setIsLeaving] = useState(false);

  useEffect(() => {
    if (arrivingFromLogoBack) {
      return;
    }
    if (shouldReduceMotion) {
      setIsZoomed(true);
      return;
    }
    const timeout = setTimeout(() => setIsZoomed(true), ZOOM_IN_DELAY_MS);
    return () => clearTimeout(timeout);
  }, [arrivingFromLogoBack, shouldReduceMotion]);

  const onBack = () => {
    if (isLeaving) {
      return;
    }
    setIsLeaving(true);
    setIsZoomed(false);
    const delay = shouldReduceMotion ? 0 : ZOOM_TRANSITION_DURATION_MS;
    setTimeout(() => {
      markStudioModalTransition('dismiss');
      navigate('../choose-store', { viewTransition: true });
    }, delay);
  };

  const form = useForm<FormValuesInput, object, FormValuesOutput>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      appName: preview.appName ?? '',
      // Not part of OnboardingPreviewState (it never affects the mockup), so
      // it has to come straight from the persisted request — reading it
      // from `preview` would always default back to '' here, since nothing
      // else carries it between visits to this page.
      storeUrl: persistedRequest.storeUrl ?? '',
      appContactEmail: persistedRequest.appContactEmail ?? '',
    },
  });

  const { register, handleSubmit, formState, watch, control } = form;
  const appNameValue = watch('appName');
  const storeUrlValue = watch('storeUrl');

  // Live-updates the mockup's app name as the lead types, debounced.
  useEffect(() => {
    const timeout = setTimeout(() => {
      setPreview({ appName: appNameValue || null });
    }, 300);
    return () => clearTimeout(timeout);
  }, [appNameValue, setPreview]);

  // Debounced so the "checking" state has a moment to show before settling
  // on valid/invalid, instead of flickering straight to the result.
  const debouncedStoreUrlValue = useDebouncedValue(storeUrlValue, 300);
  const isCheckingStoreUrl =
    storeUrlValue !== debouncedStoreUrlValue && storeUrlValue.length > 0;
  const storeUrlCheck = checkStoreUrl(debouncedStoreUrlValue ?? '');

  const onSubmit = handleSubmit(async (values) => {
    await patchCreationRequest.mutateAsync({
      appName: values.appName,
      storeUrl: values.storeUrl,
      appContactEmail: values.appContactEmail,
    });
    navigate('../app-logo', { viewTransition: true });
  });

  return (
    <OnboardingPageLayout
      mobileFocus={isZoomed ? HOME_ICON_FOCUS : undefined}
      mockup={
        <ZoomedIPhoneMockup sceneKey="home" chromeBaked zoomed={isZoomed}>
          <HomeScreenScene
            appName={preview.appName}
            appIconUrl={preview.appIconUrl}
          />
        </ZoomedIPhoneMockup>
      }
    >
      <OnboardingStepsIndicator currentStep="app-name-link" />

      <motion.div
        initial={shouldReduceMotion ? false : { opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <span className="mb-3 inline-flex items-center gap-1.5 rounded-full bg-secondary px-3 py-1 text-xs font-medium text-secondary-foreground max-lg:hidden">
          <Sparkles className="h-3 w-3" />
          Identidade do app
        </span>
        <OnboardingPageHeader
          title="Como seu app vai se chamar?"
          subtitle="Esse nome vai aparecer na tela inicial do celular. Capriche, é a primeira impressão da sua marca."
        />
      </motion.div>

      <OnboardingPageContent>
        <motion.form
          id="app-name-and-link-form"
          onSubmit={onSubmit}
          initial={shouldReduceMotion ? false : { opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.08 }}
        >
          {/* Below lg the form sits in the Studio's sheet, already a surface of
              its own: no card chrome, and no card title repeating the page
              title, so the fields fit above the fold. */}
          <Card className="rounded-2xl border-border/60 shadow-sm transition-shadow focus-within:shadow-md focus-within:shadow-primary/5 max-lg:rounded-none max-lg:border-0 max-lg:bg-transparent max-lg:shadow-none max-lg:focus-within:shadow-none">
            <CardHeader className="max-lg:hidden">
              <CardTitle>Nome e link da loja</CardTitle>
            </CardHeader>
            <CardContent className="space-y-5 max-lg:p-0">
              <LabeledInput
                label="Nome do app"
                labelFor="appName"
                error={formState.errors.appName?.message}
              >
                <Input
                  id="appName"
                  placeholder="Ex: Korza Studio"
                  className="h-11 text-base font-medium"
                  // Not below `lg`: there the merchant first sees the app on
                  // the home screen, and focusing would scroll the sheet past
                  // the question (or open the keyboard, on some iPhones).
                  autoFocus={isDesktop}
                  {...register('appName')}
                />
              </LabeledInput>

              <LabeledInput
                label="Link da sua loja"
                labelFor="storeUrl"
                error={formState.errors.storeUrl?.message}
              >
                <Controller
                  name="storeUrl"
                  control={control}
                  render={({ field }) => (
                    <div className="relative">
                      <Input
                        id="storeUrl"
                        placeholder="minhaloja.com.br"
                        className="h-11 pr-9"
                        {...field}
                        onChange={(e) =>
                          field.onChange(maskStoreUrl(e.target.value))
                        }
                      />
                      <div className="absolute inset-y-0 right-3 flex items-center">
                        <AnimatePresence mode="wait" initial={false}>
                          {isCheckingStoreUrl ? (
                            <motion.span
                              key="checking"
                              initial={{ scale: 0.6, opacity: 0 }}
                              animate={{ scale: 1, opacity: 1 }}
                              exit={{ scale: 0.6, opacity: 0 }}
                              transition={{ duration: 0.15 }}
                            >
                              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                            </motion.span>
                          ) : storeUrlCheck.status === 'valid' ? (
                            <motion.span
                              key="valid"
                              initial={{ scale: 0.6, opacity: 0 }}
                              animate={{ scale: 1, opacity: 1 }}
                              exit={{ scale: 0.6, opacity: 0 }}
                              transition={{
                                type: 'spring',
                                stiffness: 500,
                                damping: 20,
                              }}
                            >
                              <CheckCircle2 className="h-4 w-4 text-primary" />
                            </motion.span>
                          ) : storeUrlCheck.status === 'invalid' ? (
                            <motion.span
                              key="invalid"
                              initial={{ scale: 0.6, opacity: 0 }}
                              animate={{ scale: 1, opacity: 1 }}
                              exit={{ scale: 0.6, opacity: 0 }}
                              transition={{ duration: 0.15 }}
                            >
                              <AlertCircle className="h-4 w-4 text-amber-500" />
                            </motion.span>
                          ) : null}
                        </AnimatePresence>
                      </div>
                    </div>
                  )}
                />
                {!formState.errors.storeUrl &&
                  !isCheckingStoreUrl &&
                  storeUrlCheck.status === 'invalid' && (
                    <p className="text-sm text-amber-600">
                      {storeUrlCheck.message}
                    </p>
                  )}
              </LabeledInput>

              <LabeledInput
                label={
                  <TooltipProvider delayDuration={150}>
                    <span className="inline-flex items-center gap-1.5">
                      Email de contato do app
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Info className="h-3.5 w-3.5 cursor-default text-muted-foreground" />
                        </TooltipTrigger>
                        <TooltipContent className="max-w-64">
                          Esse email aparece como contato oficial do app nas
                          lojas (App Store e Google Play) e é usado para
                          comunicados sobre o app.
                        </TooltipContent>
                      </Tooltip>
                    </span>
                  </TooltipProvider>
                }
                labelFor="appContactEmail"
                error={formState.errors.appContactEmail?.message}
              >
                <Input
                  id="appContactEmail"
                  type="email"
                  placeholder="contato@minhaloja.com.br"
                  className="h-11"
                  {...register('appContactEmail')}
                />
              </LabeledInput>
            </CardContent>
          </Card>
        </motion.form>
      </OnboardingPageContent>

      <OnboardingPageFooter className="justify-between">
        <Button
          type="button"
          variant="outline"
          disabled={patchCreationRequest.isPending || isLeaving}
          onClick={onBack}
        >
          Voltar
        </Button>
        <Button
          type="submit"
          form="app-name-and-link-form"
          disabled={patchCreationRequest.isPending || isLeaving}
          className="min-w-[100px]"
        >
          {patchCreationRequest.isPending ? (
            <Loader2 className="animate-spin" />
          ) : (
            'Continuar'
          )}
        </Button>
      </OnboardingPageFooter>
    </OnboardingPageLayout>
  );
}
